/**
  * @class meleTable
  * @description
  *
  * # Purpose
  * Implements `<mele-table>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-table></mele-table>';
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-table',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleList from 'meleList'
import ResizeObserver from 'resize-observer-polyfill'
const mxlsx = require('msysXLSX')

const PROPERTIES = _.extend({}, {
  columns: { type: 'array', description: 'Column keys in table' },
  rowattributes: { type: 'array', description: "definition of additional row attributes calculated from item's fields" },
  selected: { type: 'array', description: 'list of keys of selected items' },
  collapse: { type: 'array', description: 'list of keys of collapsed items' },
  bulk: { type: 'array', description: 'Array of bulk action definitions (key, label, icon). Enables bulk selection UI and renders action buttons' },
  bulkselected: { type: 'array', description: 'List of keys of bulk-selected items' },
  eventSelected: { type: 'function', description: 'method that can be overwritten to inform about change of selected items; default will publish mmListSelected' },
  eventCellClicked: { type: 'function', description: 'method that can be overwritten to inform about click on table cell' },
  eventBulkSelected: { type: 'function', description: 'method called when bulk selection changes' },
  eventBulkAction: { type: 'function', description: 'method called when bulk action button is clicked' },
  role: { type: 'string', description: 'view role' },
  responsive: { type: 'boolean', description: 'table rows will have label before each cell', default: false },
}, meleList._properties)

let td_template, th_template

export default class meleTable extends meleList {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    if(!td_template) td_template = this.createElementFast('td')
    if(!th_template) th_template = this.createElementFast('th')
    super.init()
    this._columns_wrapper = this.createElementFast('mele-header')
    this._columns_wrapper.setAttribute('key', 'columns')
    this._header_wrapper.appendChild(this._columns_wrapper)

    // create bulk checkbox header cell (initially hidden)
    this._bulk_checkbox_th = th_template.cloneNode()
    this._bulk_checkbox_th.className = 'bulk-checkbox'
    this._bulk_checkbox_th.hidden = true
    const checkbox = this.createElementFast('input')
    checkbox.type = 'checkbox'
    checkbox.onclick = (e) => {
      e.stopPropagation()
      this.toggleBulkAll(e.target.checked)
    }
    this._bulk_checkbox_th.appendChild(checkbox)

    this._resizeObserver = new ResizeObserver(() => {
      this.refreshTableWidths()
      setTimeout(()=>this.refreshTableWidths(), 500)
    })
    this.refreshHiddenHeaderCopy()
    this.refreshTableWidths()
  } // init

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * special handling to convert attributes to canonical form
  */
  setProperty(prop, value, context_for_changed_callback, force_if_notchanged, skip_update_time) {
    if(prop==='columns') {
      // convert "columns" parameter to canonical form
      value = Array.isArray(value) ? value : []
      value = value.map(col => {
        col = _.isObjectLike(col) ? col : { key: col }
        col.key = _.toString(col.key)
        col.label = _.toString(col.label)
        return col
      })
    }else if(prop==='rowattributes') {
      if(!Array.isArray(value)) value = []
      value = value.map(attr=>_.toString(attr)).filter(attr=>attr!=='')
    }
    super.setProperty(prop, value, context_for_changed_callback, force_if_notchanged, skip_update_time)
  } // setProperty

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "columns"
  */
  columnsChanged(columns) {
    this.translateAndSetDependencies('x', false, 'columns')

    // recreate table header
    let user_dependent = false
    let app_dependent = false

    // ensure bulk checkbox th is first (if bulk is enabled)
    const hasBulk = Array.isArray(this.bulk) && this.bulk.length > 0
    if(hasBulk && this._bulk_checkbox_th.parentElement !== this._columns_wrapper) {
      this._columns_wrapper.insertBefore(this._bulk_checkbox_th, this._columns_wrapper.firstChild)
    } else if(!hasBulk && this._bulk_checkbox_th.parentElement === this._columns_wrapper) {
      this._bulk_checkbox_th.remove()
    }

    // remove unnecessary cells (skip bulk checkbox th)
    const startIdx = hasBulk ? 1 : 0
    while(this._columns_wrapper.children.length > columns.length + startIdx) this._columns_wrapper.lastChild.remove()

    const fragment = this.ownerDocument.createDocumentFragment()
    for(let i=0; i<columns.length; i++) {
      const col = columns[i]

      // calculate column features
      if(/^user\./.test(col.prefix)) user_dependent = true
      if(/^app\./.test(col.prefix)) app_dependent = true
      if(!col.hidden && col.hidewhen) {
        const re = new RegExp(col.hidewhen)
        col.temp_hidden = re.test(this.role)
      }
      if(col.timeformat) col.timeformat = this.translate(col.timeformat)
      if(col.timefromstring) col.timefromstring = this.translate(col.timefromstring)

      // calculate label
      const label = col.label ? this.translate(col.label, false) : col.key

      // get or create table cell (offset by startIdx for bulk checkbox)
      let th = this._columns_wrapper.children[i + startIdx]
      if(!th) {
        th = th_template.cloneNode()
        fragment.appendChild(th)
      }

      // fill the value
      if(th.innerHTML !== label) th.innerHTML = label

      // hide if desired
      if(col.hidden || col.temp_hidden) {
        if(th.style.display !== 'none') th.style.display = 'none'
      }else{
        if(th.style.display) th.style.removeProperty('display')
      }
    }
    this._columns_wrapper.appendChild(fragment)

    // recreate table rows
    this.forAll('.mele-row', el=>el.render())
    this.refreshHiddenHeaderCopy()
    this.refreshTableWidths()

    this.classList.toggle('user-dependent', user_dependent) // special class indicating dependency on user translations
    this.classList.toggle('app-dependent', app_dependent) // special class indicating dependency on app translations
  } // columnsChanged

  roleChanged(value) {
    if(!/none/.test(value)) {
      this.columnsChanged(this.columns, this.columns)
    }
  }

  responsiveChanged(value) {
    // if(value) {
    // }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "bulk"
  */
  bulkChanged(bulk) {
    const hasBulk = Array.isArray(bulk) && bulk.length > 0

    // show/hide bulk checkbox column
    this._bulk_checkbox_th.hidden = !hasBulk

    // re-render rows to add/remove checkboxes
    this.forAll('.mele-row', el => el.render(this.responsive))
    this.refreshHiddenHeaderCopy()
    this.refreshTableWidths()
  } // bulkChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "bulkselected"
  */
  bulkselectedChanged(value, old) {
    // update row checkbox states
    this.forAll('.mele-row', el => {
      el.bulkselected = value.includes(el.key)
    })

    // update header checkbox state
    this._updateBulkHeaderCheckbox()

    // fire event
    this.eventBulkSelected(this, value, old)
  } // bulkselectedChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Update header checkbox state (checked/unchecked/indeterminate)
  */
  _updateBulkHeaderCheckbox() {
    const checkbox = this._bulk_checkbox_th.querySelector('input')
    if(!checkbox) return

    const visibleRows = this._body.findAll('>mele-row:not([hidden])')
    const visibleKeys = visibleRows.map(el => el.key).filter(k => k)
    const selectedVisible = visibleKeys.filter(k => this.bulkselected.includes(k))

    if(selectedVisible.length === 0) {
      checkbox.checked = false
      checkbox.indeterminate = false
    } else if(selectedVisible.length === visibleKeys.length) {
      checkbox.checked = true
      checkbox.indeterminate = false
    } else {
      checkbox.checked = false
      checkbox.indeterminate = true
    }
  } // _updateBulkHeaderCheckbox

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Toggle bulk selection for a single item
  */
  toggleBulkSelected(key, toggle) {
    if(_.isNil(key)) return
    key = _.toString(key)
    const current = [...this.bulkselected]
    const idx = current.indexOf(key)
    const newStatus = _.isNil(toggle) ? idx < 0 : !!toggle

    if(newStatus && idx < 0) {
      this.bulkselected = [...current, key]
    } else if(!newStatus && idx >= 0) {
      this.bulkselected = current.filter(k => k !== key)
    }
  } // toggleBulkSelected

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Toggle bulk selection for all visible items
  */
  toggleBulkAll(toggle) {
    const visibleRows = this._body.findAll('>mele-row:not([hidden])')
    const visibleKeys = visibleRows.map(el => el.key).filter(k => k)

    if(toggle) {
      // add all visible keys to selection
      const current = new Set(this.bulkselected)
      visibleKeys.forEach(k => current.add(k))
      this.bulkselected = [...current]
    } else {
      // remove all visible keys from selection
      this.bulkselected = this.bulkselected.filter(k => !visibleKeys.includes(k))
    }
  } // toggleBulkAll

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Clear all bulk selections
  */
  clearBulkSelected() {
    if(this.bulkselected.length > 0) {
      this.bulkselected = []
    }
  } // clearBulkSelected

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Default handler for bulk selection change
  */
  eventBulkSelected(self, bulkselected, old) {
    this.publish('mmTableBulkSelected', { holder: this.key, selected: bulkselected.join('∫') })
  } // eventBulkSelected

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Default handler for bulk action button click
  */
  eventBulkAction(self, action, bulkselected) {
    this.publish('mmTableBulkAction', { holder: this.key, action, selected: bulkselected.join('∫') })
  } // eventBulkAction

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Override searchChanged to clear bulk selection
  */
  searchChanged(value) {
    this.clearBulkSelected()
    super.searchChanged(value)
  } // searchChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Override filterChanged to clear bulk selection
  */
  filterChanged(value) {
    this.clearBulkSelected()
    super.filterChanged(value)
  } // filterChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "rowattributes"
  */
  rowattributesChanged() {
    // recreate table rows
    this.forAll('.mele-row', el=>el.render(this.responsive))
  } // rowattributesChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Re-translates element
  */
  retranslate() {
    this.languageChanged()
    this.rowattributesChanged()
  } // retranslate

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Transform item to canonical form
  */
  coerceItem(item) {
    if(!_.isObjectLike(item)) { return null }
    item = _.extend({
      tag: 'mele-row',
    }, item)
    return item
  } // coerceItem

  _renderChangeRows(elements) {
    (Array.isArray(elements) ? elements : [elements]).forEach(el=>{
      try{ el.render(this.responsive) }catch(e) {}
    })
    this.scrollToSelected()
  }

  prependItems(items, before_item) {
    const result = super.prependItems(items, before_item)
    this._renderChangeRows(result)
    return result
  }

  appendItems(items, after_item) {
    const result = super.appendItems(items, after_item)
    this._renderChangeRows(result)
    return result
  }

  updateItems(items) {
    const result = super.updateItems(items)
    this._renderChangeRows(result)
    return result
  }

  setItemsFromArray(array_of_arrays, key_column) {
    const columns = this.columns.map(col=>col.key)
    if(!Array.isArray(array_of_arrays)) array_of_arrays = []
    const items = []
    array_of_arrays.forEach(arr=>{
      if(!Array.isArray(arr)) return
      const data = {}
      columns.forEach((col, idx)=>data[col]=arr[idx])
      const key = data[key_column||'id']
      items.push({ key, data })
    })
    this.setItems(items)
  }

  setItemsFromString(str, row_delim, col_delim, key_column) {
    const columns = this.columns.map(col=>col.key)
    str = _.toString(str)
    if(str.length==0) return this.deleteItems()
    const rows = str.split(row_delim)
    const items = []
    rows.forEach(row=>{
      row = _.toString(row)
      if(row.length==0) return
      const arr = row.split(col_delim)
      const data = {}
      columns.forEach((col, idx)=>data[col]=arr[idx])
      const key = data[key_column||'id']
      items.push({ key, data })
    })
    this.setItems(items)
  }

  formStatusChanged() {
    // in mele-table we don't need to calculate changed/invalid/conflict flags
    // but we need to ensure hidden row with content copied from table header
    // and recalculate table header cells width according to body columns
    this.refreshHiddenHeaderCopy()
    this.refreshTableWidths()
  } // formStatusChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * called when body cellss change; cause change of table header cells width
  */
  refreshHiddenHeaderCopy() {
    // create duplicate of table header row
    if(!this._hidden_header) {
      this._hidden_header = this.createElementFast('mele-row-hidden')
      this._hidden_header.setAttribute('key', ':comber')
      this._body.appendChild(this._hidden_header)
    }

    if(!this._columns_wrapper) return

    const hasBulk = Array.isArray(this.bulk) && this.bulk.length > 0
    const startIdx = hasBulk ? 1 : 0
    const totalCells = this.columns.length + startIdx

    while(this._hidden_header.children.length > totalCells) this._hidden_header.lastChild.remove()

    const fragment = this.ownerDocument.createDocumentFragment()

    // handle bulk checkbox td
    if(hasBulk) {
      let bulkTd = this._hidden_header.children[0]
      if(!bulkTd || !bulkTd.classList.contains('bulk-checkbox')) {
        bulkTd = td_template.cloneNode()
        bulkTd.className = 'bulk-checkbox'
        this._resizeObserver.observe(bulkTd)
        if(this._hidden_header.firstChild) {
          this._hidden_header.insertBefore(bulkTd, this._hidden_header.firstChild)
        } else {
          this._hidden_header.appendChild(bulkTd)
        }
      }
    } else {
      const firstChild = this._hidden_header.children[0]
      if(firstChild && firstChild.classList.contains('bulk-checkbox')) {
        firstChild.remove()
      }
    }

    for(let i=0; i<this.columns.length; i++) {
      const col = this.columns[i]
      const th = this._columns_wrapper.children[i + startIdx]

      // get or create table cell
      let td = this._hidden_header.children[i + startIdx]
      if(!td) {
        td = td_template.cloneNode()
        this._resizeObserver.observe(td)
        fragment.appendChild(td)
      }

      // fill the value
      if(th && td.innerHTML != th.innerHTML) td.innerHTML = th.innerHTML

      // hide if desired
      if(col.hidden || col.temp_hidden) {
        if(th && th.style.display != 'none') th.style.display = 'none'
        if(td.style.display != 'none') td.style.display = 'none'
      }else{
        if(th && th.style.display) th.style.removeProperty('display')
      }
    }
    this._hidden_header.appendChild(fragment)
  } // refreshHiddenHeaderCopy

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * called when body cellss change; cause change of table header cells width
  */
  refreshTableWidths() {
    if(!this._columns_wrapper) return
    const heads = this._columns_wrapper.findAll('th')
    const first_row = this.find('mele-row-hidden')
    const samples = this.findAll('mele-row-hidden >td')
    const styles = ['width', 'borderLeftWidth', 'borderRightWidth']
    for(let i=0; i<heads.length; i++) {
      const computed = samples[i] ? getComputedStyle(samples[i]) : null
      for(const s of styles) heads[i].style[s] = computed ? computed[s] : ''
    }
    const computed = first_row ? getComputedStyle(first_row) : null
    for(const s of styles) {
      if(s === 'width') continue
      this._columns_wrapper.style[s] = computed ? computed[s] : ''
    }
  } // refreshTableWidths

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * opens new browser window with print-version of table
  * given template shall be HTML where following placeholders are supported:
  * ${key<N>} - replaced by N-th column key
  * ${label<N>} - replaced by N-th column displayed localized label
  * ${value<N>} - replaced by N-th cell for particular record
  * ${label:<key>} - replaced by displayed localized label of column with key <key>
  * ${value:<key>} - replaced by displayed localized value of column with key <key>
  * ${idx} - replaced by record's order
  */
  templateRows(template) {
    template = _.toString(template)
    if(template==='') return ''

    const keys = this.columns.map(c=>c.key)
    const key_idx = {}
    keys.forEach((k, idx)=>{ key_idx[k]=idx+1 })
    const labels = this._columns_wrapper.findAll('th').filter(th=>!th.classList.contains('bulk-checkbox')).map(th=>th.innerText)

    const hasBulk = Array.isArray(this.bulk) && this.bulk.length > 0
    const offset = _.natural(this.pagingoffset)+1
    return this._body.findAll('>mele-row').filter(el=>!!el.key).map((row, idx)=>{
      const values = row.findAll('td').filter(td=>!td.classList.contains('bulk-checkbox')).map(td=>td.innerText)
      return template
        .replace(/\$\{idx}/g, offset+idx)
        .replace(/\$\{key(\d+)\}/g, (m, m1)=>_.toString(keys[m1-1]))
        .replace(/\$\{label(\d+)\}/g, (m, m1)=>_.toString(labels[m1-1]))
        .replace(/\$\{value(\d+)\}/g, (m, m1)=>_.toString(values[m1-1]))
        .replace(/\$\{label:([^}]+)\}/g, (m, m1)=>_.toString(labels[key_idx[m1]-1]))
        .replace(/\$\{value:([^}]+)\}/g, (m, m1)=>_.toString(values[key_idx[m1]-1]))
        .replace(/\$\{rawvalue:([^}]+)\}/g, (m, m1)=>_.toString(row.data[m1]))
        .replace(/\$\{isempty:([^}]+)\}/g, (m, m1)=>_.toString(row.data[m1])==''?1:0)
    }).join('')
  } // templateRows

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Creates a download link and triggers download for the given content
  * @param {string|Blob} content - content to be downloaded
  * @param {string} mimeType - MIME type of the content
  * @param {string} filename - name of the file to be downloaded
  */
  createDownloadLink(content, mimeType, filename) {
    const blob = new Blob([content], { type: mimeType })
    const href = URL.createObjectURL(blob)
    const el = document.createElement('a')
    el.setAttribute('href', href)
    el.setAttribute('download', filename)
    document.body.appendChild(el)
    el.click()
    document.body.removeChild(el)
    URL.revokeObjectURL(href)
  } // createDownloadLink

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * CSV export for the table
  * @param {string} filename - name of the file to be downloaded
  * @param {string} [separator] - column separator; default is ','
  */
  exportCSV(filename, separator) {
    filename = filename || 'export.csv'
    separator = separator || ','
    const columns = this.columns.filter(col=>!col.img).map(col=>col.label)
    const exported = this.columns.map(col=>col.img ? 0 : 1)
    const hasBulk = Array.isArray(this.bulk) && this.bulk.length > 0
    const startIdx = hasBulk ? 1 : 0
    const csvLines = []
    csvLines.push(columns.map(col=>`"${String(col??'').replace(/"/g, '""')}"`).join(separator))
    for(const el of this.findAll('mele-row')) {
      const cells = []
      for(let i=0; i<exported.length; i++) {
        if(exported[i]) {
          let val = String(el.children[i + startIdx]?.innerText ?? '')
          val = `"${val.replace(/"/g, '""')}"`
          cells.push(val)
        }
      }
      csvLines.push(cells.join(separator))
    }
    const csvContent = csvLines.join('\r\n')
    this.createDownloadLink(csvContent, 'text/csv;charset=utf-8;', filename)
  } // exportCSV

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * JSON export for the table
  * @param {string} filename - name of the file to be downloaded
  */
  exportJSON(filename) {
    const items = this.getItemElements()
    const data = items.map(item=>item.data)
    this.createDownloadLink(JSON.stringify(data, null, 2), 'application/json', filename)
  } // exportJSON

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * XML export for the table
  * @param {string} filename - name of the file to be downloaded
  */
  exportXML(filename) {
    filename = filename || 'export.xml'
    const items = this.getItemElements()
    let xmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n<items>\n'
    for(const item of items) {
      xmlContent += '  <item>\n'
      for(const key in item.data) {
        let val = String(item.data[key] ?? '')
        val = val.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;')
        xmlContent += `    <${key}>${val}</${key}>\n`
      }
      xmlContent += '  </item>\n'
    }
    xmlContent += '</items>\n'
    this.createDownloadLink(xmlContent, 'application/xml', filename)
  } // exportXML

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * XLSX export for the table
  * @param {string} filename - name of the file to be downloaded
  */
  async exportXLSX(filename) {
    filename = filename || 'export.xlsx'

    const columns = this.columns.filter(col=>!col.img).map(col=>col.label)
    const exported = this.columns.map(col=>col.img ? 0 : 1)
    const hasBulk = Array.isArray(this.bulk) && this.bulk.length > 0
    const startIdx = hasBulk ? 1 : 0

    const sheet = {
      name: 'Sheet1',
      freezeX: 0,
      freezeY: 1,
      rows: [],
    }

    sheet.rows.push(columns.map(col=>[col, 'header']))

    for(const el of this.findAll('mele-row')) {
      const row = []
      for(let i=0; i<exported.length; i++) {
        if(exported[i]) {
          const val = String(el.children[i + startIdx]?.innerText ?? '')
          row.push([val, ''])
        }
      }
      sheet.rows.push(row)
    }

    const jszip_options = { type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
    const xls = { sheets: [sheet] }
    const blob = await mxlsx.compose(xls, jszip_options)
    this.createDownloadLink(blob, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', filename)
  } // exportXLSX

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * stringification of table
  */
  toText() {
    const columns = []
    const rows = []
    this._columns_wrapper.forAll('th', el=>{
      if(!el.classList.contains('bulk-checkbox')) columns.push(el.innerText)
    })
    this._body.forAll('mele-row:not([hidden])', el=> rows.push(el.toText()))
    return {
      columns,
      rows,
    }
  }
} // class meleTable

meleTable._properties = PROPERTIES
meleTable.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-table', meleTable)
